import { PriceZoneSystem } from '@schuelerhilfe/shared/src/enums/priceZoneSystem';
import {
  buildZonePriceMap,
  findActivePriceZone,
} from '@schuelerhilfe/shared/src/pricing/priceZones';
import type { LocationDto } from '@/lib/api';
import type { DomainKey } from '@/lib/domain-config';

/**
 * Resolves a price from the location's price zones by matching
 * `pricePlaceholder` against `price.placeholder` across all products
 * in the active system's zone. First match wins.
 *
 * System selection: `SH` for de/at/ch, `ERNA` for ien (ErsteNachhilfe).
 * Returns a formatted price string (e.g. "9,35 €") or null if not found.
 * An absent `pricePlaceholder` is not an error — since SH-415 a column may
 * carry a literal `fixedPrice` instead — so it resolves to null like any
 * other miss, and the caller falls back.
 *
 * Used by the Pricing Teaser (per-column price).
 */
export function resolvePrice(
  location: LocationDto | undefined,
  pricePlaceholder: string | undefined,
  domainKey: DomainKey,
): string | null {
  if (!pricePlaceholder) {
    return null;
  }
  const system =
    domainKey === 'ien' ? PriceZoneSystem.ERNA : PriceZoneSystem.SH;
  const zone = findActivePriceZone(location?.priceZones, system);
  if (!zone) {
    return null;
  }

  return buildZonePriceMap(zone).get(pricePlaceholder) ?? null;
}
